import React, { useCallback, useEffect, useState } from 'react';
import {
  Content,
  Header,
  Page,
  Link,
  EmptyState,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { formatDistance } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Task {
  id: string;
  templateName: string;
  status: 'completed' | 'failed' | 'running' | 'processing' | 'open' | 'cancelled';
  createdAt: string;
  completedAt?: string;
  duration?: number;
  createdBy: string;
  entityRef?: string;
}

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CFG: Record<
  string,
  { label: string; color: string; bg: string; border: string; icon: React.ReactNode }
> = {
  completed: {
    label: 'DONE',
    color: '#00B37E',
    bg: '#00B37E12',
    border: '#00B37E30',
    icon: <CheckCircleIcon sx={{ fontSize: 13 }} />,
  },
  failed: {
    label: 'FAILED',
    color: '#E8445A',
    bg: '#E8445A12',
    border: '#E8445A30',
    icon: <ErrorIcon sx={{ fontSize: 13 }} />,
  },
  processing: {
    label: 'RUNNING',
    color: '#0E7AFE',
    bg: '#0E7AFE12',
    border: '#0E7AFE30',
    icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} />,
  },
  running: {
    label: 'RUNNING',
    color: '#0E7AFE',
    bg: '#0E7AFE12',
    border: '#0E7AFE30',
    icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} />,
  },
  open: {
    label: 'QUEUED',
    color: '#635BFF',
    bg: '#635BFF12',
    border: '#635BFF30',
    icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} />,
  },
  cancelled: {
    label: 'CANCELLED',
    color: '#999',
    bg: '#99991210',
    border: '#99993030',
    icon: <ErrorIcon sx={{ fontSize: 13 }} />,
  },
};

// ─── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.completed;
  const isLive = status === 'processing' || status === 'running' || status === 'open';
  return (
    <Chip
      icon={cfg.icon as any}
      label={cfg.label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: cfg.color,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 0.75,
        '& .MuiChip-label': { pl: 0.25, pr: 0.75 },
        '& .MuiChip-icon': { fontSize: 12, color: `${cfg.color} !important`, ml: 0.5 },
        ...(isLive && {
          animation: 'pulse 1.8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 },
          },
        }),
      }}
    />
  );
}

// ─── Duration formatter ───────────────────────────────────────────────────────

function formatDuration(ms?: number): string {
  if (!ms) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// ─── Task row ─────────────────────────────────────────────────────────────────

function TaskRow({ task }: { task: Task }) {
  const isLive = task.status === 'processing' || task.status === 'running' || task.status === 'open';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.1,
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background 0.1s',
      }}
    >
      <StatusBadge status={task.status} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Link to={`/tasks/${task.id}`} style={{ textDecoration: 'none' }}>
          <Typography
            variant="body2"
            fontWeight={500}
            fontSize={13}
            noWrap
            title={task.templateName}
            sx={{ color: 'text.primary', '&:hover': { color: '#0E7AFE' } }}
          >
            {task.templateName}
          </Typography>
        </Link>
        {task.entityRef && (
          <Typography variant="caption" color="text.disabled" fontSize={11} noWrap>
            {task.entityRef}
          </Typography>
        )}
      </Box>

      {task.duration && (
        <Typography variant="caption" color="text.disabled" fontSize={11} sx={{ flexShrink: 0 }}>
          {formatDuration(task.duration)}
        </Typography>
      )}

      <Tooltip title={new Date(task.createdAt).toLocaleString()} placement="left">
        <Typography
          variant="caption"
          color="text.secondary"
          fontSize={11}
          sx={{ flexShrink: 0, minWidth: 52, textAlign: 'right' }}
        >
          {formatDistance(new Date(task.createdAt), new Date(), { addSuffix: true })}
        </Typography>
      </Tooltip>

      <Tooltip title={isLive ? 'View live task' : 'View task details'} placement="left">
        <Link to={`/tasks/${task.id}`} style={{ display: 'flex' }}>
          <IconButton size="small" sx={{ p: 0.4, color: 'text.secondary', '&:hover': { color: '#0E7AFE' } }}>
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Link>
      </Tooltip>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export const TasksPage = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const load = useCallback(async () => {
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('tasks');
      const resp = await fetchApi.fetch(`${baseUrl}/user-tasks`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      const data = await resp.json();
      setTasks(data.tasks ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, refreshKey]);

  // Auto-refresh every 30s if any tasks are running
  const hasLive = tasks.some(
    t => t.status === 'processing' || t.status === 'running' || t.status === 'open',
  );
  useEffect(() => {
    if (!hasLive) return;
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [hasLive, load]);

  // Tab filtering
  const running = tasks.filter(t => ['processing', 'running', 'open'].includes(t.status));
  const completed = tasks.filter(t => t.status === 'completed');
  const failed = tasks.filter(t => t.status === 'failed' || t.status === 'cancelled');
  const shown = tab === 0 ? tasks : tab === 1 ? running : tab === 2 ? completed : failed;

  return (
    <Page themeId="tool">
      <Header title="My Tasks" subtitle="Template runs and their full deployment pipeline status" />
      <Content>
        {error ? (
          <Box>
            <Typography color="error" mb={2}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => setRefreshKey(k => k + 1)}>
              Retry
            </Button>
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} fontSize={14} lineHeight={1.2}>
                    Template Runs
                  </Typography>
                  <Typography variant="caption" color="text.disabled" fontSize={11} lineHeight={1.2}>
                    Click any task to view the full deployment pipeline status
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tasks.length > 0 && (
                  <Chip
                    label={tasks.length}
                    size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, '& .MuiChip-label': { px: 1 } }}
                  />
                )}
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => setRefreshKey(k => k + 1)}
                    sx={{ p: 0.5 }}
                  >
                    <RefreshIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Link to="/create">
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 12, textTransform: 'none', borderRadius: 1.5 }}
                  >
                    New template run
                  </Button>
                </Link>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: 36,
                '& .MuiTab-root': { minHeight: 36, fontSize: 12, py: 0, textTransform: 'none', px: 2 },
              }}
            >
              <Tab label={`All (${tasks.length})`} />
              <Tab
                label={
                  running.length > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Running
                      <Chip
                        label={running.length}
                        size="small"
                        color="info"
                        sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    </Box>
                  ) : (
                    'Running'
                  )
                }
              />
              <Tab label={`Completed (${completed.length})`} />
              <Tab
                label={
                  failed.length > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Failed
                      <Chip
                        label={failed.length}
                        size="small"
                        color="error"
                        sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    </Box>
                  ) : (
                    'Failed'
                  )
                }
              />
            </Tabs>

            {/* Content */}
            {loading ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
                    <Skeleton variant="rounded" width={64} height={20} sx={{ borderRadius: 0.75 }} />
                    <Skeleton variant="text" width={`${40 + (i % 4) * 12}%`} height={18} />
                    <Skeleton variant="text" width={48} height={16} sx={{ ml: 'auto' }} />
                    <Skeleton variant="circular" width={22} height={22} />
                  </Box>
                ))}
              </Box>
            ) : shown.length === 0 ? (
              <EmptyState
                missing="data"
                title={tab === 0 ? 'No tasks yet' : `No ${['', 'running', 'completed', 'failed'][tab]} tasks`}
                description="Run a template from Self-Service to see your tasks here."
                action={
                  tab === 0 ? (
                    <Link to="/create">
                      <Button variant="contained" color="primary">
                        Browse Templates
                      </Button>
                    </Link>
                  ) : undefined
                }
              />
            ) : (
              <Box>
                {shown.map((task, i) => (
                  <Box key={task.id}>
                    {i > 0 && <Divider />}
                    <TaskRow task={task} />
                  </Box>
                ))}
              </Box>
            )}

            {/* Footer */}
            {tasks.length > 0 && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="caption" color="text.disabled" fontSize={11}>
                  {tasks.length} task{tasks.length !== 1 ? 's' : ''} total
                  {hasLive && ' · auto-refreshing'}
                </Typography>
                <Link to="/create">
                  <Typography variant="caption" color="primary" fontSize={11}>
                    Browse templates →
                  </Typography>
                </Link>
              </Box>
            )}
          </Paper>
        )}
      </Content>
    </Page>
  );
};
